// Thin wrapper around gtag/dataLayer. Every event is auto-stamped with
// `platform: "web"` so GA4 funnels can filter web traffic out of the same
// property the mobile app (Firebase) reports into.
//
// Safe to call before gtag is ready or before consent is granted — the
// queue lives on window.dataLayer (set up in public/index.html) and
// Consent Mode v2 holds events back until the user accepts.

const PLATFORM = "web";

const hasDataLayer = () =>
  typeof window !== "undefined" && Array.isArray(window.dataLayer);

// Fire a custom event. Pushes a NAMED { event, ...params } object onto
// dataLayer — this is the only shape GTM recognises as a trigger.
// Using gtag("event", ...) instead would write the args positionally and
// GTM would silently ignore the custom name (you'd only see auto events
// like page_view / user_engagement / scroll land in GA4).
//
// Keep event names snake_case to match GA4 conventions and read
// consistently next to the mobile Firebase events.
export const trackEvent = (eventName, params = {}) => {
  if (!eventName) return;
  if (!hasDataLayer()) {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    } else {
      return;
    }
  }
  window.dataLayer.push({
    event: eventName,
    platform: PLATFORM,
    ...params,
  });
};

// SPA pageview — call on every route change. We pass page_location and
// page_path so GA4 picks them up the same way it would on a hard load.
export const trackPageView = (path) => {
  if (!hasDataLayer()) {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    } else {
      return;
    }
  }
  const resolvedPath =
    path ||
    (typeof window !== "undefined" && window.location
      ? window.location.pathname + window.location.search
      : "");
  const location =
    typeof window !== "undefined" && window.location
      ? window.location.href
      : resolvedPath;
  window.dataLayer.push({
    event: "page_view",
    platform: PLATFORM,
    page_location: location,
    page_path: resolvedPath,
    page_title:
      typeof document !== "undefined" ? document.title : undefined,
  });
};

// Hooks once into history so every SPA navigation fires a page_view.
// Safe to call multiple times — guarded by a flag.
let historyPatched = false;
export const initRouteTracking = () => {
  if (typeof window === "undefined") return;
  if (historyPatched) return;
  historyPatched = true;

  // Fire the initial pageview (the GTM container fires its own on load too,
  // but we want ours to carry the platform stamp so the dataLayer-driven
  // tag and our tag agree).
  trackPageView();

  const wrap = (method) => {
    const original = window.history[method];
    window.history[method] = function patched(...args) {
      const result = original.apply(this, args);
      // Defer so document.title has updated.
      setTimeout(() => trackPageView(), 0);
      return result;
    };
  };
  wrap("pushState");
  wrap("replaceState");
  window.addEventListener("popstate", () => trackPageView());
};
